import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, TrendingUp } from "lucide-react";

export default function AIProgressAdmin() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/admin/ai-progress/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/ai-progress/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${r.data.uploaded} models. Chart data replaced.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="ai-progress-admin">
      <div className="card-surface rounded-xl p-6">
        <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-5 h-5 text-[#00F0FF]" /><h3 className="font-display font-bold text-lg">AI Progress Over Time Data</h3></div>
        <p className="text-sm text-[#94A3B8]">Powers the "How fast is AI actually improving?" chart. Re-upload a CSV (columns: <span className="font-mono-tech text-[#e2e8f0]">Score, Date, Company, Country, Confidence, Model, Source, Source URL, License</span>) to update with new model releases. Only rows with a numeric Score and valid Date (YYYY-MM-DD) are plotted.</p>

        {stats && (
          <div className="mt-4 flex flex-wrap gap-6">
            <div><div className="font-display font-bold text-2xl text-[#00F0FF]" data-testid="ai-progress-total">{stats.total}</div><div className="text-xs text-[#94A3B8] font-mono-tech uppercase">models plotted</div></div>
            <div><div className="font-display font-bold text-2xl">{stats.companies}</div><div className="text-xs text-[#94A3B8] font-mono-tech uppercase">companies</div></div>
            {stats.top && <div><div className="font-display font-bold text-2xl">{stats.top.score}</div><div className="text-xs text-[#94A3B8] font-mono-tech uppercase">top: {stats.top.model}</div></div>}
            {stats.latest && <div><div className="font-display font-bold text-2xl">{stats.latest.date}</div><div className="text-xs text-[#94A3B8] font-mono-tech uppercase">latest: {stats.latest.model}</div></div>}
          </div>
        )}

        <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" data-testid="ai-progress-file" />
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ai-progress-upload-btn"
          className="mt-5 inline-flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-5 py-2.5 rounded-md hover:brightness-110 disabled:opacity-50">
          <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Re-upload CSV"}
        </button>
        <p className="mt-3 text-xs text-[#64748B]">⚠️ This replaces all existing AI progress data with the uploaded file.</p>
      </div>
    </div>
  );
}
